/**
 * Pure loot-item roller for Battle v2. Given a deterministic pair of
 * roll numbers (from combat-core's LootRoll) + the enemy tier + zone,
 * returns a droppable item stub or null.
 *
 * Deliberately simpler than tickCombat's full loot system — enough to
 * make battles feel rewarding while we finish the port. tickCombat keeps
 * using its inline generator; this module is for the new settle path
 * only, so no existing code path shifts.
 */

import { rollRarity, rollItemLevel, scaleStats, powerOf, type Rarity } from "@/lib/game/loot";

export type LootDrop = {
  name: string;
  kind: "weapon" | "armor" | "accessory";
  slot: string;
  rarity: Rarity;
  level: number;
  stats: Record<string, number>;
  power: number;
};

const NAMES = {
  weapon: ["Pulsar-Säbel", "Chrom-Klinge", "Void-Dolch", "Datastrahl-Bogen"],
  armor_helm: ["Chrom-Visier", "Pixel-Haube"],
  armor_chest: ["Holo-Brustplatte", "Synapse-Kürass"],
  armor_pants: ["Servo-Hose", "Phasen-Schurz"],
  armor_boots: ["Vektor-Stiefel", "Neon-Sneaker"],
  armor_gloves: ["Datakern-Handschuhe", "Chrom-Gauntlets"],
  accessory: ["Code-Amulett", "Phasen-Ring", "Echo-Kristall"],
} as const;

const ARMOR_SLOTS = ["helm", "chest", "pants", "boots", "gloves"] as const;

/** Wurf-Grundmenge: alle Basis-Slots gleichwahrscheinlich (je 1/7). */
const SLOT_ROLL = ["weapon", ...ARMOR_SLOTS, "accessory"] as const;

function pick<T>(arr: readonly T[], roll: number): T {
  return arr[Math.floor(roll * arr.length) % arr.length];
}

/**
 * Chance auf einen Ausrüstungs-Drop pro getötetem Gegner, je Tier.
 *
 * Steht hier als eigene, reine Funktion, weil die Anzeige dieselbe Zahl nennen
 * muss, die `rollLootItem` unten würfelt: Gegnerkarte (`BattleCards.tsx`) und
 * Tier-Pille (`Badges.tsx`) lesen sie hier, statt sie im Erklärtext
 * nachzuerzählen. Vorher stand dort „Auf 0 bringen heißt Beute" — bei einem
 * normalen Gegner eine Zusage, die in vier von fünf Kämpfen nicht eingelöst
 * wird.
 *
 * ⚠️ Sie sagt nur, ob der WURF trifft. Ob ein Boss überhaupt gewürfelt wird,
 * entscheidet zusätzlich die Frontier-Regel im Settle-Layer
 * (`tier === "boss" && !isFrontier` → gar kein Wurf).
 */
export function dropChanceFor(enemyTier: "normal" | "elite" | "boss"): number {
  return enemyTier === "boss" ? 1 : enemyTier === "elite" ? 0.45 : 0.2;
}

export function rollLootItem(
  roll: number,
  rarityRoll: number,
  enemyTier: "normal" | "elite" | "boss",
  zone: number,
): LootDrop | null {
  // Not every kill drops — 20% for normal, 45% elite, 100% boss.
  //
  // Halbiert am 02.09.2026 (Sign-off Stefan): normal 0.4 → 0.2, elite 0.7 →
  // 0.45, Boss unveraendert 1.0. Grund ist NICHT die Staerke des Loots — die
  // stimmt (gemessen ~2,3 Level-Aequivalente pro Zone gegen ~5 Level aus XP,
  // also 46 % gegenueber Stefans Zielmarke „rund 50 % dessen, was Leveln
  // bringt"). Das Problem war das Rauschen: 67 % aller Drops waren keine
  // Verbesserung. Weniger, dafuer bedeutsamere Drops.
  //
  // Preis der Zeile (nachgemessen 03.09.2026 ueber 300 Journeys je Fall,
  // `scripts/loot-gegenprobe.ts` — die frueher hier notierten „rund 31 %" trafen
  // keinen der Faelle): Das Loot-Gold sinkt um 33 % im Modell „alles verkaufen"
  // und um 42,5 % fuer den Spieler, der nur Muell verkauft. MIT eingeschaltetem
  // Auto-Salvage liegt der passive Spieler dagegen 55 % UEBER heute. Gold ist der
  // einzige Schmiede-Input; die Schmiede war in keinem Zustand innerhalb einer
  // Journey zu stemmen (11–38 Journeys je Set-Level) und bleibt es.
  const dropChance = dropChanceFor(enemyTier);
  if (roll > dropChance) return null;

  // Gewuerfelt wird ueber die SIEBEN Slots gleichverteilt, nicht ueber die drei
  // Kategorien: Die alte Fassung teilte in drei gleiche Toepfe (weapon / armor /
  // accessory), aber der armor-Topf traegt fuenf Teile — jedes einzelne
  // Ruestungsteil bekam so nur 1/15, eine Waffe 1/3. Gemessen ueber 200k Wuerfe:
  // weapon 36 %, accessory 28 %, jedes Ruestungsteil ~7 %.
  //
  // ⚠️ Der Slot-Wurf MUSS aus `rarityRoll` kommen, nicht aus `roll`. `roll` hat
  // ueber der Zeile schon den Drop entschieden und ist danach auf
  // [0, dropChance) gestaucht — `(roll * 7) % 1` umrundet den Kreis dann nur
  // `dropChance * 7` mal und belegt den Anfang doppelt. Bei Normal (0,2 × 7 =
  // 1,4 Umlaeufe) ergab das weapon/helm 20,4 %, chest 18,4 %, der Rest 10,2 % —
  // Faktor 2,0 statt der hier zugesagten Gleichverteilung, und zwar ausgerechnet
  // in der Tier-Klasse, die die meisten Drops stellt. `rarityRoll` ist ein
  // eigener, unabhaengiger `rng.next()` aus dem Combat-Core (simulate.ts:548)
  // und haengt an keinem Filter; `rollRarity`/`rollItemLevel` ziehen aus
  // `Math.random()` und brauchen ihn nicht. Gemessen nach der Umstellung:
  // 14,2–14,3 % auf allen sieben Slots (Faktor 1,007).
  //
  // Hash-neutral: der Wert wurde vom Core ohnehin schon gewuerfelt und
  // uebergeben — es kommt kein Wurf hinzu, der Core bleibt unangetastet (v10).
  const slotIdx = Math.floor(rarityRoll * SLOT_ROLL.length) % SLOT_ROLL.length;
  return makeItemForSlot(
    SLOT_ROLL[slotIdx],
    rollRarity(enemyTier),
    rollItemLevel(zone, enemyTier),
    roll,
  );
}

/** Die sieben Grund-Slots — Reihenfolge ist der Wurf-Raum von `rollLootItem`. */
export const EQUIP_SLOT_ROLL = SLOT_ROLL;

/**
 * Baut EIN Ausruestungsteil fuer einen bekannten Slot.
 *
 * Herausgeloest am 07.09.2026, weil jetzt zwei Wege ein Teil erzeugen: der
 * Beute-Wurf hier und die Ausruestung, die ein Gegner traegt
 * (`enemy-gear.ts`). Beide muessen dieselben Namen, Basiswerte und
 * Skalierungen benutzen — ein zweiter Tabellensatz waere genau das Duplikat,
 * das im Grundkonzept unter „nie nachbauen" steht.
 *
 * `nameRoll` waehlt nur den Namen aus dem Topf des Slots und hat keinen
 * Einfluss auf die Werte.
 */
export function makeItemForSlot(
  rolledSlot: (typeof SLOT_ROLL)[number],
  rarity: Rarity,
  level: number,
  nameRoll: number,
): LootDrop {
  const kind: LootDrop["kind"] =
    rolledSlot === "weapon" ? "weapon" : rolledSlot === "accessory" ? "accessory" : "armor";

  let slot: string;
  let name: string;
  let baseStats: Record<string, number>;
  if (kind === "weapon") {
    slot = "weapon";
    name = pick(NAMES.weapon, (nameRoll * 13) % 1);
    baseStats = { atk: 2 };
  } else if (kind === "armor") {
    // Der Sub-Slot steht schon fest — nicht erneut wuerfeln, sonst waere die
    // Gleichverteilung des Slot-Wurfs wieder dahin.
    const sub = rolledSlot as (typeof ARMOR_SLOTS)[number];
    slot = sub;
    const pool = NAMES[`armor_${sub}` as const];
    name = pick(pool, (nameRoll * 23) % 1);
    baseStats =
      sub === "chest"
        ? { def: 2, hp_max: 6 }
        : sub === "helm"
          ? { def: 1, hp_max: 4 }
          : sub === "pants"
            ? { def: 1, hp_max: 3, spd: 1 }
            : sub === "boots"
              ? { def: 1, spd: 1 }
              : { def: 1, atk: 1 };
  } else {
    slot = "accessory";
    name = pick(NAMES.accessory, (nameRoll * 29) % 1);
    baseStats = { spd: 1, atk: 1 };
  }
  const stats = scaleStats(baseStats, rarity, level);
  return { name, kind, slot, rarity, level, stats, power: powerOf(stats) };
}
